import React from "react";
import { useReportActionPermissions } from "@core/reports/hooks/useReportActionPermissions";
import {
  buildReportPermissionFlags,
  type ReportPermissionFlags,
} from "@core/reports/utils/reportPermissionFlags";
import {
  canApproveDepartmentReport,
  canValidateDepartmentReport,
  isCurrentUserSubmitter,
  isDepartmentReportEditable,
  normalizeReportWorkflowStatus,
} from "@core/reports/utils/reportWorkflow";
import { useAuthStore } from "@features/auth/store/authStore";

type LeadershipScope = "church" | "district" | "nation";

export function useLeadershipReportPermissions({
  scope,
  scopeId,
  departmentId,
  statusName,
  submittedBy,
  enabled = true,
  /** When true (default for church pages that need it), also load REVIEW_ACTIVITY_ENTRY. */
  includeActivityEntry = false,
}: {
  scope: LeadershipScope;
  scopeId: number;
  departmentId: number;
  statusName: string;
  submittedBy?: number | string | null;
  enabled?: boolean;
  includeActivityEntry?: boolean;
}) {
  const workflowEditable = isDepartmentReportEditable(statusName);
  const currentUserId = useAuthStore((s) => s.getAuthUser()?.id ?? null);
  const isOwnReport = isCurrentUserSubmitter(currentUserId, submittedBy);

  const {
    loading: permissionsLoading,
    canSave,
    canSubmit,
    canReview,
    canApprove,
    canApproveOwn,
    canValidate,
    canUploadMedia,
    canRemoveMedia,
    canReviewActivity,
  } = useReportActionPermissions({
    scope,
    scopeId,
    departmentId,
    includeActivityEntry,
    enabled: enabled && scopeId > 0 && departmentId > 0,
  });

  const permissionFlags: ReportPermissionFlags = React.useMemo(
    () =>
      buildReportPermissionFlags(
        workflowEditable,
        {
          canSave,
          canSubmit,
          canReview,
          canApprove,
          canApproveOwn,
          canValidate,
          canUploadMedia,
          canRemoveMedia,
        },
        {
          canValidateByWorkflow: canValidateDepartmentReport(statusName),
          canApproveByWorkflow: canApproveDepartmentReport(statusName),
          canManualValidateByWorkflow:
            normalizeReportWorkflowStatus(statusName) === "SUBMITTED",
          isOwnReport,
        }
      ),
    [
      workflowEditable,
      canSave,
      canSubmit,
      canReview,
      canApprove,
      canApproveOwn,
      canValidate,
      canUploadMedia,
      canRemoveMedia,
      statusName,
      isOwnReport,
    ]
  );

  return {
    workflowEditable,
    permissionFlags,
    canReviewActivity: includeActivityEntry ? canReviewActivity : false,
    permissionsLoading: enabled ? permissionsLoading : false,
    actionsVisible: enabled ? !permissionsLoading : false,
  };
}
